import type { AcousticCalibrationSnapshot, AcousticCalibrationStore, AcousticCalibrationTag } from './calibrationTypes';
import { ACOUSTIC_DECISION_RADAR_AXES } from './calibrationTypes';

function normalizeSnapshot(snapshot: AcousticCalibrationSnapshot): AcousticCalibrationSnapshot {
  const decisionDefaults = Object.fromEntries(
    ACOUSTIC_DECISION_RADAR_AXES.map((axis) => [axis.key, 0])
  ) as AcousticCalibrationSnapshot['decisionRadar'];
  const merged = { ...decisionDefaults, ...snapshot.decisionRadar };
  merged.integrated_single_speaker =
    snapshot.decisionRadar?.integrated_single_speaker ?? snapshot.situationScores.single_speaker;
  merged.integrated_conversation =
    snapshot.decisionRadar?.integrated_conversation ?? snapshot.situationScores.conversation;
  merged.integrated_babble = snapshot.decisionRadar?.integrated_babble ?? snapshot.situationScores.babble;
  merged.integrated_shouting =
    snapshot.decisionRadar?.integrated_shouting ?? snapshot.situationScores.shouting;
  return { ...snapshot, decisionRadar: merged };
}

export const ACOUSTIC_CALIBRATION_STORAGE_KEY = 'acousticCalibrationSnapshots.v1';
export const ACOUSTIC_CALIBRATION_UPDATED_EVENT = 'acoustic-calibration-updated';

function emptyStore(): AcousticCalibrationStore {
  return {
    version: 1,
    updatedAt: new Date().toISOString(),
    snapshots: []
  };
}

export function readCalibrationStore(): AcousticCalibrationStore {
  if (typeof window === 'undefined') return emptyStore();
  try {
    const raw = window.localStorage.getItem(ACOUSTIC_CALIBRATION_STORAGE_KEY);
    if (!raw) return emptyStore();
    const parsed = JSON.parse(raw) as AcousticCalibrationStore;
    if (parsed?.version !== 1 || !Array.isArray(parsed.snapshots)) return emptyStore();
    parsed.snapshots = parsed.snapshots.map((snap) => normalizeSnapshot(snap));
    return parsed;
  } catch {
    return emptyStore();
  }
}

type WriteCalibrationOptions = { persistRemote?: boolean; persistMode?: 'snapshot' | 'full' };

function writeCalibrationStore(store: AcousticCalibrationStore, options?: WriteCalibrationOptions): void {
  const next: AcousticCalibrationStore = {
    ...store,
    updatedAt: new Date().toISOString(),
    snapshots: store.snapshots.map((snap) => normalizeSnapshot(snap))
  };
  window.localStorage.setItem(ACOUSTIC_CALIBRATION_STORAGE_KEY, JSON.stringify(next));
  window.dispatchEvent(new CustomEvent(ACOUSTIC_CALIBRATION_UPDATED_EVENT));
  if (options?.persistRemote === false || typeof window === 'undefined') return;
  if (options?.persistMode === 'full') {
    void import('../acousticCalibrationApi')
      .then(({ replaceAcousticSnapshotsOnServer }) => replaceAcousticSnapshotsOnServer(next.snapshots))
      .catch(() => {});
    return;
  }
  const last = next.snapshots[next.snapshots.length - 1];
  if (last) {
    void import('../acousticCalibrationApi')
      .then(({ saveAcousticSnapshotToServer }) => saveAcousticSnapshotToServer(last))
      .catch(() => {});
  }
}

export function replaceCalibrationStore(
  store: AcousticCalibrationStore,
  options?: WriteCalibrationOptions
): AcousticCalibrationStore {
  const next: AcousticCalibrationStore = {
    ...emptyStore(),
    ...store,
    snapshots: Array.isArray(store.snapshots) ? store.snapshots.map((snap) => normalizeSnapshot(snap)) : []
  };
  writeCalibrationStore(next, options);
  return next;
}

export function listCalibrationSnapshots(): AcousticCalibrationSnapshot[] {
  return readCalibrationStore().snapshots.slice().sort((a, b) => b.taggedAt.localeCompare(a.taggedAt));
}

export function addCalibrationSnapshot(snapshot: AcousticCalibrationSnapshot): AcousticCalibrationSnapshot[] {
  const store = readCalibrationStore();
  store.snapshots.push(normalizeSnapshot(snapshot));
  writeCalibrationStore(store);
  return listCalibrationSnapshots();
}

export function deleteCalibrationSnapshot(id: string): AcousticCalibrationSnapshot[] {
  const store = readCalibrationStore();
  store.snapshots = store.snapshots.filter((entry) => entry.id !== id);
  writeCalibrationStore(store, { persistRemote: false });
  void import('../acousticCalibrationApi')
    .then(({ deleteAcousticSnapshotOnServer }) => deleteAcousticSnapshotOnServer(id))
    .catch(() => {});
  return listCalibrationSnapshots();
}

export function clearCalibrationSnapshots(): void {
  writeCalibrationStore(emptyStore(), { persistRemote: false });
  void import('../acousticCalibrationApi')
    .then(({ clearAcousticSnapshotsOnServer }) => clearAcousticSnapshotsOnServer())
    .catch(() => {});
}

export function countSnapshotsByTag(
  snapshots: AcousticCalibrationSnapshot[]
): Record<AcousticCalibrationTag, number> {
  const counts: Record<AcousticCalibrationTag, number> = {
    single_speaker: 0,
    conversation: 0,
    babble: 0,
    shouting: 0,
    silence: 0
  };
  for (const snapshot of snapshots) {
    counts[snapshot.tag] += 1;
  }
  return counts;
}

export function exportCalibrationJson(): string {
  return JSON.stringify(readCalibrationStore(), null, 2);
}

export function importCalibrationJson(raw: string): { ok: true; count: number } | { ok: false; error: string } {
  try {
    const parsed = JSON.parse(raw) as AcousticCalibrationStore;
    if (parsed?.version !== 1 || !Array.isArray(parsed.snapshots)) {
      return { ok: false, error: 'Ungültiges Kalibrierungsformat' };
    }
    writeCalibrationStore(parsed, { persistMode: 'full' });
    return { ok: true, count: parsed.snapshots.length };
  } catch {
    return { ok: false, error: 'JSON konnte nicht gelesen werden' };
  }
}
